import React from "react";
import {useNavigate, useOutletContext} from "react-router";
import {Alert, Box} from "@mui/material";
import type {GridFilterModel} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {useDataGridQueryState} from "~/components/data-grid/useDataGridQueryState";
import type {ContractLifecycle, ContractSummaryResponse} from "~/pages/contract-explorer/contractExplorerTypes";
import {LIFECYCLES} from "~/pages/contract-explorer/contractExplorerTypes";
import {useContractSummaryQuery} from "~/pages/contract-explorer/useContractExplorerQueries";
import {useSupplierContractsQuery} from "~/pages/supplier-360/tabs/SupplierContracts/hooks/useSupplierContractsQuery";
import type {SupplierDetailOutletContext} from "~/pages/supplier-360/types/Supplier360Types";
import {contractExplorerLink, getSupplierContractColumns} from "./SupplierContractColumns";

/** Highest spend first, like every Supplier 360 table. */
const INITIAL_SORT_MODEL = [{field: 'linked_spend', sort: 'desc'}] as const;

/** Stable fallback while the summary is loading, so the columns memo doesn't churn. */
const NO_TYPE_COUNTS: ContractSummaryResponse['type_counts'] = [];

function filterValue(model: GridFilterModel, field: string): string | undefined {
    const value = model.items.find(item => item.field === field)?.value;
    return value ? String(value) : undefined;
}

export const SupplierContractsTab: React.FC = () => {
    const {entity} = useOutletContext<SupplierDetailOutletContext>();
    const navigate = useNavigate();
    const gridState = useDataGridQueryState({
        initialSortModel: [...INITIAL_SORT_MODEL],
        resetPageOn: entity.id,
    });

    const lifecycle = filterValue(gridState.filterModel, 'lifecycle');
    const {data, isFetching, isError, error} = useSupplierContractsQuery({
        databagId: entity.databag_id,
        supplierName: entity.canonical_name,
        page: gridState.page,
        pageSize: gridState.pageSize,
        ordering: gridState.ordering,
        lifecycle: LIFECYCLES.includes(lifecycle as ContractLifecycle) ? lifecycle as ContractLifecycle : undefined,
        documentType: filterValue(gridState.filterModel, 'document_type'),
    });

    // The Type filter's options: the contract types present in this databag.
    const {data: summary} = useContractSummaryQuery(entity.databag_id);
    const typeCounts = summary?.type_counts ?? NO_TYPE_COUNTS;
    const columns = React.useMemo(
        () => getSupplierContractColumns(entity.databag_id, typeCounts),
        [entity.databag_id, typeCounts],
    );

    if (isError) {
        return <Alert severity="error">{`Could not load contracts. ${(error as Error)?.message ?? ''}`}</Alert>;
    }

    return (
        <Box sx={{height: '100%', flex: 1}}>
            <CustomDataGrid
                layoutKey="supplier-360-contracts"
                rows={data?.rows ?? []}
                columns={columns}
                loading={isFetching}
                rowCount={data?.rowCount ?? 0}
                {...gridState.dataGridProps}
                getRowId={row => row.id}
                // The whole row opens the contract, same target as its "Open" link.
                onRowClick={({row}) => navigate(contractExplorerLink(entity.databag_id, row.id))}
                sx={{'& .MuiDataGrid-row': {cursor: 'pointer'}}}
                showToolbar
                disableRowSelectionOnClick
                toolbarProps={{
                    title: `${data?.rowCount ?? 0} contract${data?.rowCount === 1 ? '' : 's'}`,
                    showFilterPanel: false,
                }}
                // The grid stays up when there's nothing to show, so the filters remain reachable.
                localeText={{
                    noRowsLabel: gridState.hasActiveFilters
                        ? 'No contracts match the current filters.'
                        : 'No contracts found for this supplier.',
                }}
            />
        </Box>
    );
};
